import { Box, Typography } from "@mui/material";
import { Linkedin, LucideProps, Mail, MapPin, Phone } from "lucide-react";
import { FC, ReactNode } from "react";

const BasicDetail = ({
  label,
  content,
  alwaysShow = false,
  horizontal = false,
}: {
  label: string;
  content: ReactNode | string | undefined;
  alwaysShow?: boolean;
  horizontal?: boolean;
}) => {
  if (!content && !alwaysShow) return null;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: horizontal ? "row" : "column",
        gap: horizontal ? "8px" : "2px",
      }}
    >
      <Typography variant="bodyHeavy">{label}</Typography>
      {content ? (
        <Typography variant="body">{content}</Typography>
      ) : (
        <Typography variant="body" color="#8E9598">
          None provided
        </Typography>
      )}
    </Box>
  );
};

const ContactDetail = ({
  icon,
  content,
}: {
  icon: "Phone" | "Mail" | "MapPin" | "Linkedin" | undefined;
  content: ReactNode | string | undefined;
}) => {
  const contactIcons = {
    Phone: <Phone size={20} color={"#8E9598"} />,
    Mail: <Mail size={20} color={"#8E9598"} />,
    MapPin: <MapPin size={20} color={"#8E9598"} />,
    Linkedin: <Linkedin size={20} color={"#8E9598"} />,
  };

  return (
    <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
      {icon && contactIcons[icon]}
      <Typography variant="body" sx={{ width: "100%" }}>
        {content}
      </Typography>
    </Box>
  );
};

const MultiSelectDetail = ({
  label,
  items,
}: {
  label: string;
  Icon: FC<LucideProps>;
  items: string[] | undefined;
}) => {
  if (!items || !items.length) return null;
  return (
    <BasicDetail
      label={label}
      content={
        <Box>
          {items
            .filter((v) => v)
            .map((value) => (
              <Typography
                variant="body"
                key={value}
                sx={{ display: "flex", gap: "2px" }}
              >
                • {value.replace(/-/g, " ")}
              </Typography>
            ))}
        </Box>
      }
    />
  );
};

const TrackingDetails = ({
  trackingParameters,
}: {
  trackingParameters: Record<string, string>;
}) => {
  const keys = Object.keys(trackingParameters || {}).filter(
    (k) =>
      [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_content",
        "utm_term",
      ].indexOf(k) !== -1 && trackingParameters?.[k],
  );
  if (keys.length === 0) return null;
  return (
    <BasicDetail
      label={"Tracking"}
      content={
        <Box display={"fex"} flexDirection={"column"}>
          <Typography
            variant="bodySmall"
            sx={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            {keys
              .map((key) => {
                const value = trackingParameters?.[key];
                if (!value) return null;
                return `${key}: ${value}`;
              })
              .join("; ")}
          </Typography>
        </Box>
      }
    />
  );
};

export { BasicDetail, ContactDetail, MultiSelectDetail, TrackingDetails };
